import { Link } from "react-router-dom";
import { ExternalLink, ArrowRight, MapPin, Phone, Mail, Clock, Compass, Store, TreePine, Coffee } from "lucide-react";
import { restaurantInfo, galleryImages, testimonials } from "../mock";
import SectionHeading from "../components/SectionHeading";
import Reveal from "../components/Reveal";
import SEO, { faqSchema, localBusinessSchema, breadcrumbSchema } from "../components/SEO";
import { faqs } from "../data/faqs";
import { useT, useLang } from "../context/LanguageContext";
import { LOSGATOS_STRINGS, LOSGATOS_HISTORY, LOSGATOS_NEIGHBORS, LOSGATOS_AREAS, SEO_STRINGS } from "../i18n/strings";
import { trackEvent } from "../utils/analytics";

const iconMap = { Coffee, TreePine, Store };

export default function LosGatos() {
  const t = useT();
  const { lang } = useLang();
  const isEs = lang === "es";
  const localFaqs = faqs.filter((_, i) => [0, 1, 6, 8].includes(i));

  const combinedSchema = [
    localBusinessSchema,
    faqSchema(localFaqs, lang),
    breadcrumbSchema([
      { name: isEs ? "Inicio" : "Home", path: "/" },
      { name: "Los Gatos", path: "/los-gatos" },
    ]),
  ];

  return (
    <>
      <SEO
        title={SEO_STRINGS.losgatos.title.en}
        titleEs={SEO_STRINGS.losgatos.title.es}
        description={SEO_STRINGS.losgatos.description.en}
        descriptionEs={SEO_STRINGS.losgatos.description.es}
        path="/los-gatos"
        keywords="Mexican restaurant Downtown Los Gatos, tacos Los Gatos CA, Los Gatos Main Street restaurants, authentic Mexican Los Gatos, taqueria Los Gatos 95030, best tacos South Bay"
        keywordsEs="restaurante mexicano Downtown Los Gatos, tacos Los Gatos CA, restaurantes Main Street Los Gatos, mexicano auténtico Los Gatos, taquería Los Gatos 95030, mejores tacos South Bay"
        schema={combinedSchema}
      />

      <main className="overflow-hidden">
        {/* HERO */}
        <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 bg-cream-50">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand/10 border border-brand/25 text-[11px] uppercase tracking-[0.2em] text-brand-dark mb-6 fade-in-up">
                <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
                {t(LOSGATOS_STRINGS.eyebrow.en, LOSGATOS_STRINGS.eyebrow.es)}
              </div>
              <div className="font-hand text-2xl md:text-3xl text-brand mb-3 fade-in-up">{t(LOSGATOS_STRINGS.handEyebrow.en, LOSGATOS_STRINGS.handEyebrow.es)}</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] font-semibold text-granite-dark fade-in-up">
                {t(LOSGATOS_STRINGS.title.en, LOSGATOS_STRINGS.title.es)}
              </h1>
              <p className="mt-6 text-base md:text-lg text-granite/75 leading-relaxed max-w-xl fade-in-up">
                {t(LOSGATOS_STRINGS.sub.en, LOSGATOS_STRINGS.sub.es)}
              </p>
              <div className="mt-8 flex flex-wrap gap-3 fade-in-up">
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(restaurantInfo.location.full)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("directions_click", { source: "losgatos_hero" })}
                  data-testid="losgatos-directions-btn"
                  className="group inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-cream px-7 py-3.5 rounded-full font-semibold text-[15px] transition-colors shadow-md hover:shadow-lg"
                >
                  {t(LOSGATOS_STRINGS.directions.en, LOSGATOS_STRINGS.directions.es)}
                  <MapPin className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </a>
                <a
                  href={restaurantInfo.externalLinks.orderOnline}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("order_online_click", { source: "losgatos_hero" })}
                  data-testid="losgatos-order-btn"
                  className="inline-flex items-center gap-2 border border-granite/25 hover:border-brand hover:text-brand text-granite-dark px-7 py-3.5 rounded-full font-medium text-[15px] transition-colors"
                >
                  {t(LOSGATOS_STRINGS.orderCta.en, LOSGATOS_STRINGS.orderCta.es)} <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              {/* Quick info cards */}
              <div className="grid sm:grid-cols-3 gap-3 mt-10 max-w-lg fade-in-up">
                {[
                  { icon: MapPin, label: "49 E Main St", sub: "Los Gatos, CA 95030" },
                  { icon: Clock, label: t(LOSGATOS_STRINGS.hours.en, LOSGATOS_STRINGS.hours.es), sub: t(LOSGATOS_STRINGS.openEvery.en, LOSGATOS_STRINGS.openEvery.es) },
                  { icon: Phone, label: restaurantInfo.phone, sub: t(LOSGATOS_STRINGS.callToOrder.en, LOSGATOS_STRINGS.callToOrder.es) },
                ].map((c) => (
                  <div key={c.label} className="p-4 rounded-2xl bg-cream border border-cream-100 flex flex-col gap-1">
                    <c.icon className="w-4 h-4 text-brand mb-1" />
                    <div className="font-semibold text-sm text-granite-dark leading-tight">{c.label}</div>
                    <div className="text-xs text-granite/60">{c.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            <Reveal>
              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src={galleryImages[0]}
                    alt="Handmade tacos at Nina's Taqueria in Downtown Los Gatos"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="absolute -bottom-6 -right-4 md:-right-8 w-52 md:w-60 rounded-2xl overflow-hidden shadow-2xl border-8 border-cream">
                  <img
                    src={galleryImages[3]}
                    alt="Sope with fresh salsa at Nina's Taqueria, Los Gatos"
                    className="w-full h-full object-cover aspect-square"
                    loading="lazy"
                  />
                </div>
                <div
                  className="sticker absolute -top-4 -left-2 md:-left-6 bg-cream shadow-xl rounded-2xl px-5 py-3 cursor-default"
                  style={{ "--rot": "-4deg", transform: "rotate(-4deg)" }}
                  data-testid="sticker-losgatos-neighborhood"
                >
                  <div className="font-hand text-2xl text-brand leading-none">{t(LOSGATOS_STRINGS.stickerTitle.en, LOSGATOS_STRINGS.stickerTitle.es)}</div>
                  <div className="text-[11px] uppercase tracking-widest text-granite/70 mt-1">{t(LOSGATOS_STRINGS.stickerSub.en, LOSGATOS_STRINGS.stickerSub.es)}</div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* NEIGHBORHOOD HISTORY */}
        <section className="py-24 md:py-28 bg-cream">
          <div className="max-w-7xl mx-auto px-6">
            <Reveal>
              <SectionHeading
                eyebrow={t(LOSGATOS_STRINGS.neighborhoodEyebrow.en, LOSGATOS_STRINGS.neighborhoodEyebrow.es)}
                title={t(LOSGATOS_STRINGS.neighborhoodTitle.en, LOSGATOS_STRINGS.neighborhoodTitle.es)}
                subtitle={t(LOSGATOS_STRINGS.neighborhoodSub.en, LOSGATOS_STRINGS.neighborhoodSub.es)}
              />
            </Reveal>
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {LOSGATOS_HISTORY.map((h, i) => {
                const year = isEs ? h.year.es : h.year.en;
                const title = isEs ? h.title.es : h.title.en;
                const text = isEs ? h.text.es : h.text.en;
                return (
                  <Reveal key={title} delay={(i % 3) + 1}>
                    <div className="p-8 rounded-3xl bg-cream-50 border border-cream-100 h-full hover:border-brand/40 hover:-translate-y-1 transition-all">
                      <div className="font-hand text-brand text-2xl leading-none mb-3">{year}</div>
                      <h3 className="font-display text-xl font-semibold text-granite-dark mb-2">{title}</h3>
                      <p className="text-sm text-granite/70 leading-relaxed">{text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* NEIGHBORS WE LOVE */}
        <section className="py-24 md:py-28 bg-cream-50">
          <div className="max-w-7xl mx-auto px-6">
            <Reveal>
              <SectionHeading
                eyebrow={t(LOSGATOS_STRINGS.neighborsEyebrow.en, LOSGATOS_STRINGS.neighborsEyebrow.es)}
                title={t(LOSGATOS_STRINGS.neighborsTitle.en, LOSGATOS_STRINGS.neighborsTitle.es)}
                subtitle={t(LOSGATOS_STRINGS.neighborsSub.en, LOSGATOS_STRINGS.neighborsSub.es)}
              />
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
              {LOSGATOS_NEIGHBORS.map((n, i) => {
                const Icon = iconMap[n.icon] || Store;
                const role = isEs ? n.role.es : n.role.en;
                const blurb = isEs ? n.blurb.es : n.blurb.en;
                return (
                  <Reveal key={n.name} delay={(i % 3) + 1}>
                    <a
                      href={n.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`neighbor-link-${i}`}
                      className="group flex gap-5 p-6 rounded-3xl bg-cream border border-cream-100 hover:border-brand/40 hover:-translate-y-1 hover:shadow-md transition-all"
                    >
                      <div className="w-14 h-14 rounded-2xl bg-brand/10 text-brand flex items-center justify-center shrink-0 group-hover:bg-brand group-hover:text-cream transition-colors">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-granite/50 mb-1">{role}</div>
                        <div className="font-display text-lg font-semibold text-granite-dark group-hover:text-brand transition-colors flex items-center gap-2">
                          {n.name}
                          <ExternalLink className="w-3.5 h-3.5 opacity-50" />
                        </div>
                        <p className="text-sm text-granite/70 mt-1.5 leading-relaxed">{blurb}</p>
                      </div>
                    </a>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* AREAS SERVED */}
        <section className="py-20 md:py-24 bg-brand text-cream relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6">
            <Reveal>
              <SectionHeading
                light
                eyebrow={t(LOSGATOS_STRINGS.areaEyebrow.en, LOSGATOS_STRINGS.areaEyebrow.es)}
                title={t(LOSGATOS_STRINGS.areaTitle.en, LOSGATOS_STRINGS.areaTitle.es)}
                subtitle={t(LOSGATOS_STRINGS.areaSub.en, LOSGATOS_STRINGS.areaSub.es)}
              />
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {LOSGATOS_AREAS.map((n, i) => (
                <Reveal key={n.name} delay={(i % 3) + 1}>
                  <div className="p-5 rounded-2xl bg-granite-dark/25 backdrop-blur border border-cream/20 hover:bg-granite-dark/40 transition-colors flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Compass className="w-4 h-4 text-brand-light shrink-0" />
                      <span className="font-display text-lg">{n.name}</span>
                    </div>
                    <span className="text-xs text-cream/70">{isEs ? n.distance.es : n.distance.en}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* MAP */}
        <section className="py-20 md:py-24 bg-cream">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-2">
              <Reveal>
                <SectionHeading
                  align="left"
                  eyebrow={t(LOSGATOS_STRINGS.findEyebrow.en, LOSGATOS_STRINGS.findEyebrow.es)}
                  title={t(LOSGATOS_STRINGS.findTitle.en, LOSGATOS_STRINGS.findTitle.es)}
                  subtitle={t(LOSGATOS_STRINGS.findSub.en, LOSGATOS_STRINGS.findSub.es)}
                />
              </Reveal>
              <ul className="space-y-3 text-sm text-granite/80 mb-8">
                <li className="flex items-start gap-3"><MapPin className="w-4 h-4 text-brand mt-1 shrink-0" /><span>49 E Main Street, Los Gatos, CA 95030</span></li>
                <li className="flex items-start gap-3"><Clock className="w-4 h-4 text-brand mt-1 shrink-0" /><span>{t(LOSGATOS_STRINGS.findOpenEvery.en, LOSGATOS_STRINGS.findOpenEvery.es)}</span></li>
                <li className="flex items-start gap-3"><Phone className="w-4 h-4 text-brand mt-1 shrink-0" /><a href={`tel:${restaurantInfo.phone}`} className="hover:text-brand">{restaurantInfo.phone}</a></li>
                <li className="flex items-start gap-3"><Mail className="w-4 h-4 text-brand mt-1 shrink-0" /><a href={`mailto:${restaurantInfo.email}`} className="hover:text-brand">{restaurantInfo.email}</a></li>
              </ul>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border border-granite/25 hover:border-brand hover:text-brand text-granite-dark px-6 py-3 rounded-full font-medium text-sm transition-colors"
              >
                {t(LOSGATOS_STRINGS.contactUs.en, LOSGATOS_STRINGS.contactUs.es)} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-3">
              <div className="rounded-3xl overflow-hidden shadow-xl border border-cream-100 aspect-[4/3]">
                <iframe
                  title="Map showing Nina's Taqueria, 49 E Main Street, Los Gatos CA"
                  src="https://www.google.com/maps?q=49+E+Main+Street,+Los+Gatos,+CA+95030&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </section>

        {/* SOCIAL PROOF */}
        <section className="py-20 md:py-24 bg-cream-50">
          <div className="max-w-6xl mx-auto px-6">
            <Reveal>
              <SectionHeading
                eyebrow={t(LOSGATOS_STRINGS.lovedEyebrow.en, LOSGATOS_STRINGS.lovedEyebrow.es)}
                title={t(LOSGATOS_STRINGS.lovedTitle.en, LOSGATOS_STRINGS.lovedTitle.es)}
              />
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.slice(0, 3).map((tm, i) => (
                <Reveal key={tm.id} delay={(i % 3) + 1}>
                  <div className="p-6 rounded-2xl bg-cream border border-cream-100 h-full">
                    <p className="text-[15px] text-granite/80 leading-relaxed">“{isEs ? (tm.textEs || tm.text) : tm.text}”</p>
                    <div className="mt-5 pt-5 border-t border-cream-100">
                      <div className="font-display text-granite-dark">{tm.name}</div>
                      <div className="text-xs text-granite/55 mt-0.5">{tm.location}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA */}
        <section className="py-20 md:py-24 bg-cream">
          <div className="max-w-5xl mx-auto px-6">
            <div className="relative rounded-[32px] bg-brand-dark text-cream overflow-hidden p-10 md:p-14 text-center">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-brand-light/20 pointer-events-none" />
              <div className="absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-brand/30 pointer-events-none" />
              <div className="relative">
                <div className="font-hand text-2xl text-brand-light mb-3">{t(LOSGATOS_STRINGS.finalEyebrow.en, LOSGATOS_STRINGS.finalEyebrow.es)}</div>
                <h2 className="font-display text-3xl md:text-5xl font-semibold leading-tight">
                  {t(LOSGATOS_STRINGS.finalTitle.en, LOSGATOS_STRINGS.finalTitle.es)}
                </h2>
                <p className="mt-5 text-cream/85 max-w-xl mx-auto">
                  {t(LOSGATOS_STRINGS.finalSub.en, LOSGATOS_STRINGS.finalSub.es)}
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <a
                    href={restaurantInfo.externalLinks.orderOnline}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-cream text-brand-dark hover:bg-white px-7 py-3.5 rounded-full font-semibold text-[15px] transition-colors shadow-lg"
                  >
                    {t(LOSGATOS_STRINGS.orderCta.en, LOSGATOS_STRINGS.orderCta.es)} <ExternalLink className="w-4 h-4" />
                  </a>
                  <Link
                    to="/menu"
                    className="inline-flex items-center gap-2 border border-cream/50 hover:bg-cream/15 text-cream px-7 py-3.5 rounded-full font-medium text-[15px] transition-colors"
                  >
                    {t(LOSGATOS_STRINGS.viewMenu.en, LOSGATOS_STRINGS.viewMenu.es)} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
